/**
 * BranchFilter — Phase R10.3 (2026-07-04)
 *
 * Multi-select dropdown with checkboxes for the Owner Receipt Claims
 * dashboard. Options:
 *   - "All Branches" (toggles clear state; default)
 *   - Each dynamic branch from GET /api/owner/branches
 *   - "Unassigned" at the bottom
 *
 * Props:
 *   branches        — array of {id, name}
 *   selected        — array of selected ids ("unassigned" is a special id)
 *   onChange(ids)   — called with new array of ids
 *   loading         — bool
 */
import { useRef, useEffect, useState } from 'react';
import { ChevronDown, Check, MapPin } from 'lucide-react';

export default function BranchFilter({ branches = [], selected = [], onChange, loading = false }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const toggle = (id) => {
    const has = selected.includes(id);
    const next = has ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
  };
  const clearAll = () => onChange([]);

  const label =
    selected.length === 0
      ? 'All Branches'
      : selected.length === 1
      ? (
          selected[0] === 'unassigned'
            ? 'Unassigned'
            : branches.find((b) => b.id === selected[0])?.name ?? '1 branch'
        )
      : `${selected.length} branches`;

  return (
    <div className="relative" ref={wrapRef} data-testid="branch-filter">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={loading}
        className={
          'inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-medium transition-colors min-h-[40px] ' +
          'bg-[#15151B] border-white/10 text-white/90 hover:border-white/25 disabled:opacity-50'
        }
        data-testid="branch-filter-trigger"
      >
        <MapPin size={14} className="text-gold" />
        <span>{label}</span>
        {selected.length > 0 ? (
          <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold bg-gold text-black">
            {selected.length}
          </span>
        ) : null}
        <ChevronDown size={14} className={`text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <div
          className="absolute left-0 mt-2 w-64 max-h-80 overflow-auto rounded-2xl border border-white/10 bg-[#15151B] shadow-xl z-40"
          data-testid="branch-filter-panel"
        >
          <button
            type="button"
            onClick={clearAll}
            className={
              'w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/[0.05] ' +
              (selected.length === 0 ? 'text-gold' : 'text-white/80')
            }
            data-testid="branch-filter-option-all"
          >
            <span className="w-4 h-4 rounded border border-white/30 flex items-center justify-center">
              {selected.length === 0 ? <Check size={10} className="text-gold" /> : null}
            </span>
            All Branches
          </button>
          <div className="border-t border-white/5" />
          {branches.map((b) => {
            const isSel = selected.includes(b.id);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => toggle(b.id)}
                data-testid={`branch-filter-option-${b.id}`}
                className={
                  'w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-white/[0.05] ' +
                  (isSel ? 'text-white' : 'text-white/70')
                }
              >
                <span className={
                  'w-4 h-4 rounded border flex items-center justify-center ' +
                  (isSel ? 'bg-gold border-gold' : 'border-white/30')
                }>
                  {isSel ? <Check size={10} className="text-black" /> : null}
                </span>
                {b.name}
              </button>
            );
          })}
          <div className="border-t border-white/5" />
          <button
            type="button"
            onClick={() => toggle('unassigned')}
            data-testid="branch-filter-option-unassigned"
            className={
              'w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/[0.05] ' +
              (selected.includes('unassigned') ? 'text-white' : 'text-white/60')
            }
          >
            <span className={
              'w-4 h-4 rounded border flex items-center justify-center ' +
              (selected.includes('unassigned') ? 'bg-gold border-gold' : 'border-white/30')
            }>
              {selected.includes('unassigned') ? <Check size={10} className="text-black" /> : null}
            </span>
            Unassigned
          </button>
        </div>
      ) : null}
    </div>
  );
}
